import {
  Cached,
  Mapper,
  VersionMapper,
  ChainedMapper,
  FirestoreEntity,
  getFirestoreEntityVersionFn,
} from '../@harmony';
import { SubscriptionEntityToSubscriptionMapper } from '../purchases/domain/mappers/subscription.mapper';
import {
  AnyTeamEntityVersion,
  AnyTeamGroupEntityVersion,
  AnyTeamPublicCacheEntityVersion,
  Team,
  TeamEntity,
  TeamEntityToTeamMapper,
  TeamGroup,
  TeamGroupDto,
  TeamGroupDtoToTeamGroupEntityMapper,
  TeamGroupEntity,
  TeamGroupEntityToTeamGroupMapper,
  TeamPublicCache,
  TeamPublicCacheEntity,
  TeamPublicCacheEntityToTeamPublicCacheMapper,
} from '.';

export abstract class TeamCommonProvider {
  abstract provideTeamEntityVersionsMapper(): Mapper<
    FirestoreEntity<AnyTeamEntityVersion>,
    FirestoreEntity<TeamEntity>
  >;
  abstract provideTeamEntityToTeamMapper(): Mapper<FirestoreEntity<AnyTeamEntityVersion>, Team>;
  abstract provideTeamGroupDtoToTeamGroupMapper(): Mapper<TeamGroupDto, TeamGroup>;
  abstract provideTeamGroupEntityVersionsMapper(): Mapper<
    FirestoreEntity<AnyTeamGroupEntityVersion>,
    FirestoreEntity<TeamGroupEntity>
  >;
  abstract provideTeamGroupEntityToTeamGroupMapper(): Mapper<
    FirestoreEntity<AnyTeamGroupEntityVersion>,
    TeamGroup
  >;
  abstract provideTeamPublicCacheEntityVersionsMapper(): Mapper<
    FirestoreEntity<AnyTeamPublicCacheEntityVersion>,
    FirestoreEntity<TeamPublicCacheEntity>
  >;
  abstract provideTeamPublicCacheEntityToTeamPublicCacheMapper(): Mapper<
    FirestoreEntity<AnyTeamPublicCacheEntityVersion>,
    TeamPublicCache
  >;
}

export class TeamCommonDefaultProvider implements TeamCommonProvider {
  @Cached()
  public provideTeamEntityVersionsMapper(): Mapper<
    FirestoreEntity<AnyTeamEntityVersion>,
    FirestoreEntity<TeamEntity>
  > {
    return new VersionMapper(getFirestoreEntityVersionFn, []);
  }

  @Cached()
  public provideTeamEntityToTeamMapper(): Mapper<FirestoreEntity<AnyTeamEntityVersion>, Team> {
    return new ChainedMapper<
      [FirestoreEntity<AnyTeamEntityVersion>, FirestoreEntity<TeamEntity>, Team]
    >([
      this.provideTeamEntityVersionsMapper(),
      new TeamEntityToTeamMapper(new SubscriptionEntityToSubscriptionMapper()),
    ]);
  }

  @Cached()
  public provideTeamGroupDtoToTeamGroupMapper(): Mapper<TeamGroupDto, TeamGroup> {
    return new ChainedMapper<[TeamGroupDto, FirestoreEntity<TeamGroupEntity>, TeamGroup]>([
      new TeamGroupDtoToTeamGroupEntityMapper(),
      new TeamGroupEntityToTeamGroupMapper(),
    ]);
  }

  @Cached()
  public provideTeamGroupEntityVersionsMapper(): Mapper<
    FirestoreEntity<AnyTeamGroupEntityVersion>,
    FirestoreEntity<TeamGroupEntity>
  > {
    return new VersionMapper(getFirestoreEntityVersionFn, []);
  }

  @Cached()
  public provideTeamGroupEntityToTeamGroupMapper(): Mapper<
    FirestoreEntity<AnyTeamGroupEntityVersion>,
    TeamGroup
  > {
    return new ChainedMapper<
      [FirestoreEntity<AnyTeamGroupEntityVersion>, FirestoreEntity<TeamGroupEntity>, TeamGroup]
    >([this.provideTeamGroupEntityVersionsMapper(), new TeamGroupEntityToTeamGroupMapper()]);
  }

  @Cached()
  public provideTeamPublicCacheEntityVersionsMapper(): Mapper<
    FirestoreEntity<AnyTeamPublicCacheEntityVersion>,
    FirestoreEntity<TeamPublicCacheEntity>
  > {
    return new VersionMapper(getFirestoreEntityVersionFn, []);
  }

  @Cached()
  public provideTeamPublicCacheEntityToTeamPublicCacheMapper(): Mapper<
    FirestoreEntity<AnyTeamPublicCacheEntityVersion>,
    TeamPublicCache
  > {
    return new ChainedMapper<
      [
        FirestoreEntity<AnyTeamPublicCacheEntityVersion>,
        FirestoreEntity<TeamPublicCacheEntity>,
        TeamPublicCache,
      ]
    >([
      this.provideTeamPublicCacheEntityVersionsMapper(),
      new TeamPublicCacheEntityToTeamPublicCacheMapper(),
    ]);
  }
}
